import * as yup from 'yup';
import { ExpeditionDetailV4OutDTO } from '@api/ringil-api.v2.tsx';
import type { ExpeditionLocality } from '@app/pages/expedition/create/types.ts';

export const getAiCarrierAutoSelectionDto = (expedition: ExpeditionDetailV4OutDTO) => {
  return {
    loadingLocationId: expedition?.locations[0]?.id,
    unloadingLocationId: expedition?.locations[expedition.locations.length - 1]?.id,
    loadingCity: expedition?.locations[0]?.city,
    loadingCountry: expedition?.locations[0]?.country,
    unloadingCity: expedition?.locations[expedition.locations.length - 1]?.city,
    unloadingCountry: expedition?.locations[expedition.locations.length - 1]?.country,
    weight: expedition?.locations[0]?.loadedOrders?.reduce((sum, order) => sum + (order.items?.reduce?.((itemSum, item) => itemSum + (item.weight || 0), 0) || 0), 0) || 0,
    countPallets: expedition?.locations[0]?.loadedOrders?.reduce((sum, order) => sum + (order.items?.reduce?.((itemSum, item) => itemSum + (item.amount || 0), 0) || 0), 0) || 0,
    distance: expedition.distance,
    cargoAmount: expedition?.locations[0]?.loadedOrders?.reduce((sum, order) => sum + (order.orderAmount || 0), 0) || 0,
  };
}

export const isLadenFactorEligible = (expedition: ExpeditionDetailV4OutDTO, featureFlagOn: boolean) => {
  if (!featureFlagOn) return false;

  if (!expedition?.locations || expedition.locations.length === 0) {
    return false;
  }

  const orders = expedition.locations.flatMap(location => [...(location.loadedOrders ?? []), ...(location.unloadedOrders ?? [])]);

  return isLadenFactorEligibleByOrders(orders);
};

/**
 * Core laden-factor eligibility rule: every order must have at least one item with a positive amount.
 * Shared by the DTO variant (`isLadenFactorEligible`) and the create/edit form variant
 * (`isLadenFactorEligibleFromLocations`), which differ only in how they collect orders.
 */
export const isLadenFactorEligibleByOrders = (orders: { items?: { amount?: number }[] }[]) => {
  if (!Array.isArray(orders) || orders.length === 0) return false;

  return orders.every(order => Array.isArray(order.items) && order.items.some(item => (item?.amount ?? 0) > 0));
};

export const isLadenFactorEligibleFromLocations = (locations: ExpeditionLocality[], featureFlagOn: boolean) => {
  if (!featureFlagOn) return false;
  if (!locations || locations.length === 0) return false;

  const allOrders = locations.flatMap(loc => (Array.isArray(loc.orders) ? loc.orders : []));
  return isLadenFactorEligibleByOrders(allOrders);
};

/**
 * Single source of truth for the `ladenFactorValue` Yup field, shared by the create-expedition form
 * and the request-carrier sidebar. The LADEN_FACTOR feature gate is baked in here so call sites
 * cannot forget it. Pass `appliesToDistributionType` to additionally restrict by distribution type
 * (defaults to applying to any type).
 *
 * Expects sibling `ladenFactor` (boolean) and `distributionType` fields on the validated object.
 */
export const ladenFactorValueValidation = (
  messages: { shouldBeNumber: string; required: string },
  featureFlagOn: boolean,
  appliesToDistributionType: (distributionType: string) => boolean = () => true,
) =>
  yup.number().when(['ladenFactor', 'distributionType'], {
    is: (ladenFactor: boolean, distributionType: string) => ladenFactor === true && featureFlagOn && appliesToDistributionType(distributionType),
    then: yup
      .number()
      .typeError(messages.shouldBeNumber)
      .transform(value => (isNaN(value) ? undefined : value))
      .nullable()
      .required(messages.required),
    otherwise: yup.number().nullable(),
  });

